import { useEffect, useMemo, useState } from 'react';
import { Plus, RefreshCw, Save, Trash2, Wand2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import LoeschenMitGrundDialog from '../../../components/ui/loeschen-mit-grund-dialog';
import { marketingCouponsApi, type MarketingCouponCodeDto, type SaveMarketingCouponPayload } from '../../../lib/api';
import { api } from '../../../lib/api/core';

type PersonSearchResult = {
  id: string;
  name: string;
  email?: string;
};

const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

const euroFromCents = (cents: number): string => (cents / 100).toFixed(2).replace('.', ',');
const parseEuroToNumber = (value: string): number => {
  const normalized = value.replace(',', '.').trim();
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const generateCode = (length = 10): string => {
  let out = '';
  for (let i = 0; i < length; i += 1) {
    const idx = Math.floor(Math.random() * CODE_ALPHABET.length);
    out += CODE_ALPHABET[idx];
  }
  return out;
};

const defaults = {
  code: '',
  valueEuro: '20,00',
  singleUse: true,
  isActive: true,
  validFrom: '',
  validUntil: '',
  allowedWpUserIds: [] as number[],
};

export default function MarketingCouponCodesPage() {
  const [items, setItems] = useState<MarketingCouponCodeDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [zuLoeschen, setZuLoeschen] = useState<number | null>(null);
  const [form, setForm] = useState(defaults);

  const [userQuery, setUserQuery] = useState('');
  const [userResults, setUserResults] = useState<PersonSearchResult[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  const selectedScope = useMemo(() => new Set(form.allowedWpUserIds), [form.allowedWpUserIds]);

  // Namen der freigegebenen Benutzer — vorher stand dort nur „User #123“ (T-0217).
  const [namen, setNamen] = useState<Record<number, string>>({});
  const alleIds = useMemo(
    () => [...new Set([...items.flatMap((i) => i.allowedWpUserIds || []), ...form.allowedWpUserIds])],
    [items, form.allowedWpUserIds],
  );
  useEffect(() => {
    const fehlend = alleIds.filter((id) => !(id in namen)).slice(0, 50);
    if (fehlend.length === 0) return;
    let abgebrochen = false;
    void Promise.all(
      fehlend.map((id) =>
        api
          .get<{ name?: string; first_name?: string; last_name?: string }>(`/api/personen/${id}`)
          .then((p) => [id, p?.name || `${p?.first_name ?? ''} ${p?.last_name ?? ''}`.trim() || `Nr. ${id}`] as const)
          .catch(() => [id, `Nr. ${id}`] as const),
      ),
    ).then((paare) => {
      if (!abgebrochen) setNamen((vorher) => ({ ...vorher, ...Object.fromEntries(paare) }));
    });
    return () => {
      abgebrochen = true;
    };
  }, [alleIds, namen]);
  const nameVon = (id: number) => namen[id] ?? `Nr. ${id}`;

  const resetForm = () => {
    setEditingId(null);
    setForm(defaults);
    setUserQuery('');
    setUserResults([]);
  };

  const load = async () => {
    setLoading(true);
    try {
      const list = await marketingCouponsApi.list();
      setItems(list);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Gutscheincodes konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    const q = userQuery.trim();
    if (q.length < 2) {
      setUserResults([]);
      return;
    }
    const handle = window.setTimeout(() => {
      setUserSearchLoading(true);
      void api
        .get<PersonSearchResult[]>(`/api/personen?q=${encodeURIComponent(q)}&limit=20`)
        .then((res) => setUserResults(Array.isArray(res) ? res : []))
        .catch(() => setUserResults([]))
        .finally(() => setUserSearchLoading(false));
    }, 250);
    return () => window.clearTimeout(handle);
  }, [userQuery]);

  const startEdit = (item: MarketingCouponCodeDto) => {
    setEditingId(item.id);
    setForm({
      code: item.code,
      valueEuro: euroFromCents(item.valueCents),
      singleUse: item.singleUse,
      isActive: item.isActive,
      validFrom: item.validFrom || '',
      validUntil: item.validUntil || '',
      allowedWpUserIds: item.allowedWpUserIds || [],
    });
  };

  const save = async () => {
    const code = form.code.trim().toUpperCase();
    const valueEuro = parseEuroToNumber(form.valueEuro);
    if (!code) {
      toast.error('Bitte einen Gutscheincode eingeben');
      return;
    }
    if (valueEuro <= 0) {
      toast.error('Bitte einen gültigen Euro-Wert eingeben');
      return;
    }

    const payload: SaveMarketingCouponPayload = {
      code,
      valueEuro,
      singleUse: form.singleUse,
      isActive: form.isActive,
      validFrom: form.validFrom || null,
      validUntil: form.validUntil || null,
      allowedWpUserIds: form.allowedWpUserIds,
    };

    setSaving(true);
    try {
      if (editingId) {
        const updated = await marketingCouponsApi.update(editingId, payload);
        setItems((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
        toast.success('Gutscheincode aktualisiert');
      } else {
        const created = await marketingCouponsApi.create(payload);
        setItems((prev) => [created, ...prev]);
        toast.success('Gutscheincode erstellt');
      }
      resetForm();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const remove = (id: number) => setZuLoeschen(id);

  const loeschen = async (id: number, grund: string) => {
    await marketingCouponsApi.remove(id, grund);
    setItems((prev) => prev.filter((p) => p.id !== id));
    if (editingId === id) resetForm();
    setZuLoeschen(null);
    toast.success('Gutscheincode gelöscht');
  };

  const addScopedUser = (rawId: string) => {
    const id = Number(rawId);
    if (!id || Number.isNaN(id) || selectedScope.has(id)) return;
    setForm((prev) => ({ ...prev, allowedWpUserIds: [...prev.allowedWpUserIds, id] }));
  };

  const removeScopedUser = (id: number) => {
    setForm((prev) => ({ ...prev, allowedWpUserIds: prev.allowedWpUserIds.filter((x) => x !== id) }));
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Gutscheincodes</h1>
        <p className="text-muted-foreground">
          Erstelle und verwalte Gutscheincodes für den Kunden-Anmelde-Flow.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{editingId ? 'Gutscheincode bearbeiten' : 'Neuer Gutscheincode'}</CardTitle>
          <CardDescription>
            Standard ist einmalige Nutzung. Mit Benutzer-Scope ist ein Code pro freigegebenem Benutzer einmal nutzbar.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Code</Label>
              <div className="flex gap-2">
                <Input
                  value={form.code}
                  onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  placeholder="Z.B. FRUEHLING2026"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setForm((prev) => ({ ...prev, code: generateCode(10) }))}
                  title="Zufällig generieren"
                >
                  <Wand2 className="mr-2 h-4 w-4" />
                  Random
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Wert (EUR)</Label>
              <Input
                value={form.valueEuro}
                onChange={(e) => setForm((prev) => ({ ...prev, valueEuro: e.target.value }))}
                placeholder="20,00"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Gültig von</Label>
              <Input type="date" value={form.validFrom} onChange={(e) => setForm((prev) => ({ ...prev, validFrom: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Gültig bis</Label>
              <Input type="date" value={form.validUntil} onChange={(e) => setForm((prev) => ({ ...prev, validUntil: e.target.value }))} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <div className="text-sm font-medium">Nur einmal nutzbar</div>
                <div className="text-xs text-muted-foreground">Standard: aktiv</div>
              </div>
              <Switch checked={form.singleUse} onCheckedChange={(v) => setForm((prev) => ({ ...prev, singleUse: v }))} />
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <div className="text-sm font-medium">Aktiv</div>
                <div className="text-xs text-muted-foreground">Inaktive Codes sind ungültig</div>
              </div>
              <Switch checked={form.isActive} onCheckedChange={(v) => setForm((prev) => ({ ...prev, isActive: v }))} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Auf bestimmte Benutzer einschränken (optional)</Label>
            <Input
              placeholder="Benutzer suchen (Name, E-Mail) ..."
              value={userQuery}
              onChange={(e) => setUserQuery(e.target.value)}
            />
            {userQuery.trim().length >= 2 && (
              <div className="max-h-40 overflow-auto rounded-md border">
                {userSearchLoading ? (
                  <div className="px-3 py-2 text-sm text-muted-foreground">Suche ...</div>
                ) : userResults.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-muted-foreground">Keine Treffer</div>
                ) : (
                  userResults.map((p) => {
                    const numericId = Number(p.id);
                    const alreadyAdded = selectedScope.has(numericId);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        disabled={alreadyAdded}
                        className="flex w-full items-center justify-between border-b px-3 py-2 text-left last:border-b-0 hover:bg-accent/20 disabled:opacity-50"
                        onClick={() => {
                          addScopedUser(p.id);
                          setNamen((vorher) => ({ ...vorher, [Number(p.id)]: p.name }));
                        }}
                      >
                        <span className="text-sm">
                          {p.name} {p.email ? <span className="text-muted-foreground">({p.email})</span> : null}
                        </span>
                        <Plus className="h-4 w-4" />
                      </button>
                    );
                  })
                )}
              </div>
            )}

            {form.allowedWpUserIds.length > 0 ? (
              <div className="flex flex-wrap gap-2 pt-1">
                {form.allowedWpUserIds.map((id) => (
                  <span key={id} className="inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs">
                    {nameVon(id)}
                    <button type="button" onClick={() => removeScopedUser(id)} className="text-muted-foreground hover:text-foreground">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            ) : null}
          </div>

          <div className="flex justify-end gap-2">
            {editingId ? (
              <Button variant="outline" onClick={resetForm} disabled={saving}>
                Abbrechen
              </Button>
            ) : null}
            <Button onClick={save} disabled={saving}>
              {saving ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Speichern ...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {editingId ? 'Aktualisieren' : 'Erstellen'}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Vorhandene Gutscheincodes</CardTitle>
          <CardDescription>Liste aller Codes und Nutzung.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex justify-end">
            <Button variant="outline" onClick={load} disabled={loading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
          </div>

          <div className="divide-y rounded-md border">
            {items.map((item) => (
              <div key={item.id} className="flex flex-col gap-3 p-3 md:flex-row md:items-center md:justify-between">
                <div className="min-w-0">
                  <div className="text-sm font-semibold">{item.code}</div>
                  <div className="text-xs text-muted-foreground">
                    {euroFromCents(item.valueCents)} EUR · {item.singleUse ? 'einmalig' : 'mehrfach'} ·{' '}
                    {item.isActive ? 'aktiv' : 'inaktiv'} · Einlösungen: {item.redemptionCount}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Freigegeben für: {item.allowedWpUserIds.length > 0 ? item.allowedWpUserIds.map(nameVon).join(', ') : 'alle'}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => startEdit(item)}>
                    Bearbeiten
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => remove(item.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            {items.length === 0 && !loading ? (
              <div className="p-4 text-center text-sm text-muted-foreground">Noch keine Gutscheincodes vorhanden.</div>
            ) : null}
            {loading ? <div className="p-4 text-center text-sm text-muted-foreground">Lade ...</div> : null}
          </div>
        </CardContent>
      </Card>
      <LoeschenMitGrundDialog
        offen={zuLoeschen !== null}
        titel="Gutscheincode löschen?"
        onAbbrechen={() => setZuLoeschen(null)}
        onLoeschen={async (grund) => {
          if (zuLoeschen !== null) await loeschen(zuLoeschen, grund);
        }}
      />
    </div>
  );
}
